/**
 * Natural-language task input parsing. Pure and hand-rolled (no dep), shared
 * by the web quick-add AND the Discord bot (a message in a linked channel
 * goes through the same grammar). Scans for the LAST date phrase in the
 * string so "call may about friday's demo friday" keeps the earlier words as
 * title text.
 *
 * English-only in 1.0, and deliberately no ambiguous numerics (5/1): every
 * supported form reads one way. Supported due phrases:
 *   today, tonight, tomorrow, tmrw
 *   weekday names (mon..sunday) -> next occurrence; "next <weekday>" -> the
 *   occurrence after that; "next week" -> next Monday
 *   in N days / in N weeks
 *   jan 5 / 5 jan / january 5th (current year, rolls to next year if past)
 *   time suffixes: 9am, 9.30pm, 09:30, "at 17:00" (attach to the date phrase
 *   or stand alone, which implies today)
 * Plus recurrence phrases via parseTaskInput: every day / daily, every N
 * days, every week(s) / weekly, every weekday, every mon wed fri, every
 * month(s) / monthly.
 */
import { nextDueDate, parseRecurrence } from "./recurrence.js";
const WEEKDAYS = [
    ["sunday", "sun"],
    ["monday", "mon"],
    ["tuesday", "tue", "tues"],
    ["wednesday", "wed"],
    ["thursday", "thu", "thur", "thurs"],
    ["friday", "fri"],
    ["saturday", "sat"],
];
const MONTHS = [
    ["january", "jan"],
    ["february", "feb"],
    ["march", "mar"],
    ["april", "apr"],
    ["may"],
    ["june", "jun"],
    ["july", "jul"],
    ["august", "aug"],
    ["september", "sep", "sept"],
    ["october", "oct"],
    ["november", "nov"],
    ["december", "dec"],
];
const weekdayIndex = new Map();
WEEKDAYS.forEach((names, i) => names.forEach((n) => weekdayIndex.set(n, i)));
const monthIndex = new Map();
MONTHS.forEach((names, i) => names.forEach((n) => monthIndex.set(n, i)));
const WEEKDAY_RE = [...weekdayIndex.keys()].sort((a, b) => b.length - a.length).join("|");
const MONTH_RE = [...monthIndex.keys()].sort((a, b) => b.length - a.length).join("|");
/** 9am, 9.30pm, 9:30, 17:00, at 9pm */
const TIME_PART = String.raw `(?:at\s+)?(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?`;
const PHRASE_RE = new RegExp(String.raw `(?:^|\s)(` +
    [
        String.raw `(?:today|tonight|tomorrow|tmrw)(?:\s+${TIME_PART})?`,
        String.raw `next\s+week`,
        String.raw `(?:next\s+)?(?:${WEEKDAY_RE})(?:\s+${TIME_PART})?`,
        String.raw `in\s+(\d+)\s+(days?|weeks?)`,
        String.raw `(?:${MONTH_RE})\s+(\d{1,2})(?:st|nd|rd|th)?(?:\s+${TIME_PART})?`,
        String.raw `(\d{1,2})(?:st|nd|rd|th)?\s+(?:${MONTH_RE})(?:\s+${TIME_PART})?`,
        String.raw `at\s+(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?`,
    ].join("|") +
    String.raw `)(?=\s|$)`, "gi");
function pad(n) {
    return String(n).padStart(2, "0");
}
function iso(d) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function parseTime(text) {
    const m = new RegExp(`^${TIME_PART}$`, "i").exec(text.trim());
    if (!m)
        return undefined;
    let hours = Number(m[1]);
    const minutes = m[2] ? Number(m[2]) : 0;
    const meridiem = m[3]?.toLowerCase();
    if (hours > 23 || minutes > 59)
        return undefined;
    // a bare small number without meridiem or minutes ("at 9") is too
    // ambiguous to be worth guessing wrong; require one of the two
    if (!meridiem && !m[2])
        return undefined;
    if (meridiem === "pm" && hours < 12)
        hours += 12;
    if (meridiem === "am" && hours === 12)
        hours = 0;
    if (hours > 23)
        return undefined;
    return `${pad(hours)}:${pad(minutes)}`;
}
/** The trailing time part of a phrase like "friday 9am", if any. */
function splitTime(phrase) {
    const m = /\s+(?:at\s+)?(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm)?)$/i.exec(phrase);
    if (!m)
        return { datePart: phrase };
    const dueTime = parseTime(m[1]);
    if (!dueTime)
        return { datePart: phrase };
    return { datePart: phrase.slice(0, m.index), dueTime };
}
function resolveDate(datePart, now) {
    const p = datePart.trim().toLowerCase();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (p === "today" || p === "tonight")
        return iso(today);
    if (p === "tomorrow" || p === "tmrw")
        return iso(new Date(today.getTime() + 86_400_000));
    if (p === "next week") {
        const days = ((8 - today.getDay()) % 7) || 7; // next Monday
        return iso(new Date(today.getTime() + days * 86_400_000));
    }
    const inMatch = /^in\s+(\d+)\s+(days?|weeks?)$/.exec(p);
    if (inMatch) {
        const n = Number(inMatch[1]);
        if (n > 3650)
            return undefined;
        const days = inMatch[2].startsWith("week") ? n * 7 : n;
        return iso(new Date(today.getTime() + days * 86_400_000));
    }
    const weekdayMatch = /^(next\s+)?([a-z]+)$/.exec(p);
    if (weekdayMatch && weekdayIndex.has(weekdayMatch[2])) {
        const target = weekdayIndex.get(weekdayMatch[2]);
        let days = (target - today.getDay() + 7) % 7;
        if (days === 0)
            days = 7; // "friday" on a Friday means next Friday
        if (weekdayMatch[1])
            days += 7;
        return iso(new Date(today.getTime() + days * 86_400_000));
    }
    const monthFirst = /^([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?$/.exec(p);
    const dayFirst = /^(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]+)$/.exec(p);
    const monthName = monthFirst?.[1] ?? dayFirst?.[2];
    const dayStr = monthFirst?.[2] ?? dayFirst?.[1];
    if (monthName && dayStr) {
        const month = monthIndex.get(monthName);
        const day = Number(dayStr);
        if (month === undefined || day < 1 || day > 31)
            return undefined;
        let candidate = new Date(today.getFullYear(), month, day);
        if (candidate.getMonth() !== month)
            return undefined; // e.g. feb 31
        if (candidate.getTime() < today.getTime())
            candidate = new Date(today.getFullYear() + 1, month, day);
        return iso(candidate);
    }
    // bare time ("at 9pm") implies today
    if (parseTime(p))
        return iso(today);
    return undefined;
}
/** Find the last date phrase in the input, or null. */
export function parseDuePhrase(input, now = new Date()) {
    let last = null;
    PHRASE_RE.lastIndex = 0;
    for (let m = PHRASE_RE.exec(input); m; m = PHRASE_RE.exec(input))
        last = m;
    if (!last)
        return null;
    const phrase = last[1];
    const phraseStart = last.index + (last[0].length - phrase.length);
    const { datePart, dueTime: timeFromSuffix } = splitTime(phrase);
    let dueTime = timeFromSuffix;
    let dueDate = resolveDate(datePart, now);
    if (!dueDate) {
        // the whole phrase might be just a time ("at 9pm")
        const t = parseTime(phrase.replace(/^at\s+/i, ""));
        if (!t)
            return null;
        dueTime = t;
        dueDate = resolveDate("today", now);
    }
    const cleanTitle = (input.slice(0, phraseStart) + " " + input.slice(phraseStart + phrase.length))
        .replace(/\s+/g, " ")
        .trim();
    if (!cleanTitle)
        return null; // the entire input was the date; not a task
    return { cleanTitle, dueDate, ...(dueTime ? { dueTime } : {}), matchStart: phraseStart, matchEnd: phraseStart + phrase.length };
}
/**
 * Due-only parse for a dedicated due field (the Discord Due modal). Accepts
 * a literal "YYYY-MM-DD" (+ optional "HH:MM"), which the phrase grammar
 * rejects by design, so a prefilled value round-trips losslessly; otherwise
 * the whole input is treated as one date phrase. parseDuePhrase cannot serve
 * here: it deliberately returns null when the entire input is the date.
 */
export function parseDueOnly(input, now = new Date()) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    const isoMatch = /^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}))?$/.exec(trimmed);
    if (isoMatch) {
        return { dueDate: isoMatch[1], ...(isoMatch[2] ? { dueTime: isoMatch[2] } : {}) };
    }
    const { datePart, dueTime: timeFromSuffix } = splitTime(trimmed);
    const dueDate = resolveDate(datePart, now);
    if (dueDate)
        return { dueDate, ...(timeFromSuffix ? { dueTime: timeFromSuffix } : {}) };
    const t = parseTime(trimmed.replace(/^at\s+/i, ""));
    if (t)
        return { dueDate: resolveDate("today", now), dueTime: t };
    return null;
}
// ---------- recurrence phrases ("every ...") ----------
const RECUR_RE = new RegExp(String.raw `(?:^|\s)(` +
    [
        String.raw `daily`,
        String.raw `weekly`,
        String.raw `monthly`,
        String.raw `every\s+weekday`,
        String.raw `every\s+(?:\d+\s+)?(?:days?|weeks?|months?)`,
        String.raw `every\s+(?:${WEEKDAY_RE})(?:(?:\s*,\s*|\s+(?:and\s+)?)(?:${WEEKDAY_RE}))*`,
    ].join("|") +
    String.raw `)(?=\s|$|,)`, "gi");
function resolveRecurrence(phrase) {
    const p = phrase.toLowerCase().replace(/\s+/g, " ").trim();
    if (p === "daily")
        return parseRecurrence({ unit: "day", interval: 1 });
    if (p === "weekly")
        return parseRecurrence({ unit: "week", interval: 1 });
    if (p === "monthly")
        return parseRecurrence({ unit: "month", interval: 1 });
    if (p === "every weekday")
        return parseRecurrence({ unit: "week", interval: 1, weekdays: [1, 2, 3, 4, 5] });
    const unitMatch = /^every (?:(\d+) )?(day|week|month)s?$/.exec(p);
    if (unitMatch) {
        return parseRecurrence({ unit: unitMatch[2], interval: Number(unitMatch[1] ?? 1) });
    }
    if (p.startsWith("every ")) {
        const names = p
            .slice(6)
            .split(/\s*,\s*|\s+(?:and\s+)?/)
            .filter(Boolean);
        const weekdays = [];
        for (const name of names) {
            const idx = weekdayIndex.get(name);
            if (idx === undefined)
                return null;
            weekdays.push(idx);
        }
        if (weekdays.length)
            return parseRecurrence({ unit: "week", interval: 1, weekdays });
    }
    return null;
}
/**
 * The full quick-add grammar: an optional due phrase plus an optional
 * "every ..." recurrence clause, both stripped from the title. A recurrence
 * without an explicit due implies the first occurrence ("standup every
 * monday 9am" is due next Monday 09:00). Returns null when NOTHING was
 * recognized, or when stripping leaves no title.
 */
export function parseTaskInput(input, now = new Date()) {
    // recurrence clause first (last occurrence wins, like the due grammar)
    let recurMatch = null;
    RECUR_RE.lastIndex = 0;
    for (let m = RECUR_RE.exec(input); m; m = RECUR_RE.exec(input))
        recurMatch = m;
    let recurrence;
    let remainder = input;
    let recurRange;
    if (recurMatch) {
        const phrase = recurMatch[1];
        const start = recurMatch.index + (recurMatch[0].length - phrase.length);
        const rule = resolveRecurrence(phrase);
        if (rule) {
            recurrence = rule;
            recurRange = { start, end: start + phrase.length };
            remainder = input.slice(0, start) + " ".repeat(phrase.length) + input.slice(start + phrase.length);
        }
    }
    // due phrase on the recurrence-stripped text: offsets stay valid because
    // the clause was replaced by spaces, not removed
    const due = parseDuePhrase(remainder, now);
    const matches = [];
    if (recurRange)
        matches.push(recurRange);
    if (due)
        matches.push({ start: due.matchStart, end: due.matchEnd });
    matches.sort((a, b) => a.start - b.start);
    if (!due && !recurrence)
        return null;
    let dueDate = due?.dueDate;
    let dueTime = due?.dueTime;
    if (!dueDate && recurrence) {
        // implied first occurrence, strictly after today
        const today = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
        dueDate = nextDueDate(recurrence, undefined, today);
        // a bare trailing time in the remainder applies to the implied date
        const t = /(?:^|\s)(?:at\s+)?(\d{1,2}(?:[:.]\d{2})?\s*(?:am|pm))(?=\s|$)/i.exec(remainder);
        if (t && !dueTime) {
            const parsed = parseTime(t[1]);
            if (parsed) {
                dueTime = parsed;
                matches.push({ start: t.index + (t[0].length - t[1].length), end: t.index + t[0].length });
                remainder = remainder.slice(0, t.index) + " ".repeat(t[0].length) + remainder.slice(t.index + t[0].length);
                matches.sort((a, b) => a.start - b.start);
            }
        }
    }
    // rebuild the title by blanking every matched range in the ORIGINAL input
    const masked = due
        ? remainder.slice(0, due.matchStart) + " ".repeat(due.matchEnd - due.matchStart) + remainder.slice(due.matchEnd)
        : remainder;
    const title = masked.replace(/\s+/g, " ").trim();
    if (!title)
        return null;
    return {
        title,
        ...(dueDate ? { dueDate } : {}),
        ...(dueTime ? { dueTime } : {}),
        ...(recurrence ? { recurrence } : {}),
        matches,
    };
}
/** Compact human label for a parsed due, e.g. "Fri, Aug 22 9:00am". */
export function dueLabel(dueDate, dueTime, now = new Date()) {
    const [y, m, d] = dueDate.split("-").map(Number);
    const date = new Date(y, m - 1, d);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diff = Math.round((date.getTime() - today.getTime()) / 86_400_000);
    let day;
    if (diff === 0)
        day = "Today";
    else if (diff === 1)
        day = "Tomorrow";
    else if (diff > 1 && diff < 7)
        day = date.toLocaleDateString(undefined, { weekday: "long" });
    else
        day = date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    if (!dueTime)
        return day;
    const [hh, mm] = dueTime.split(":").map(Number);
    const t = new Date();
    t.setHours(hh, mm, 0, 0);
    return `${day} ${t.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}
//# sourceMappingURL=dateParse.js.map